import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { callFunction } from "../api/callFunction";
import { variantURL, fallbackToOriginal } from "../utils/imageVariant";
import { showToast } from "../utils/showToast";
import { GlobalContext } from "../context/GlobalContext";
import { EnlargedPhotoModal } from "../components/UserGallery";
import GuestOutreachDialog from "./GuestOutreachDialog";
import PopularExplainer from "./PopularExplainer";
import { PopularListControls, joinRateFloorNote } from "./PopularSortToggle";
import { outreachChip, outreachLine, statsLine } from "./outreachFormat";
import { eventClosedReason, hostEarnBlockedReason, hostMayUseCoupon, isAccessRevoked } from "./popularHostRules";
import { isPrimaryHost } from "./utils/eventHosts";

const LIST_LIMIT = 30;

const REVOKED_MESSAGE = "Popular guests is not turned on for your account any more.";

// The ranking still means something after the night, so the tab stays; only
// Reach out goes, because the server refuses every send on such an event.
const CLOSED_MESSAGES = {
  over: "This event is over, so you can look but not reach out. Open an upcoming event to invite people.",
  cancelled: "This event was cancelled, so you can look but not reach out.",
};

// Two of the operator tab's three scopes. "Payouts on" is a list of who Gist
// pays, which is not a host's business.
const SCOPES = [
  {
    key: "all",
    label: "All events",
    blurb: "Members across all of Gist, ranked by how many different people opened their profile from an event's guest list. Use it to pick who to invite.",
    empty: "No profile views recorded yet. Views are counted when someone opens a member's profile from an event's guest list.",
    rateBlurb: "Members across all of Gist, ranked by join rate: of the people who opened their profile from an event's guest list before joining that event, the share who then joined. Use it to find people who help others decide to come.",
    rateEmpty: "Nobody has enough people looking at them before joining to rank by join rate yet.",
  },
  {
    key: "event",
    label: "This event",
    blurb: "Who drew people to this event, ranked by how many different people opened their profile from its guest list. It fills in once people start looking.",
    empty: "Nobody's profile has been opened from this event's guest list yet.",
    rateBlurb: "Who drew people to this event, by join rate: of the people who opened their profile on its guest list before joining, the share who then joined.",
    rateEmpty: "Not enough people have looked at anyone on this event's guest list before joining to rank by join rate yet.",
  },
];

const chip = "px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide";

function Face({ guest, onExpand }) {
  return (
    <button
      type="button"
      onClick={onExpand}
      title="View photo"
      className="shrink-0 rounded-full focus:outline-none focus:ring-2 focus:ring-primary"
    >
      {guest.photoURL ? (
        <img
          src={variantURL(guest.photoURL, 200)}
          onError={fallbackToOriginal(guest.photoURL)}
          alt={guest.name || "Member"}
          loading="lazy"
          className="w-12 h-12 rounded-full object-cover shrink-0"
        />
      ) : (
        <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0">
          <span className="text-gray-500 dark:text-gray-400 text-lg">
            {(guest.name || "?").charAt(0).toUpperCase()}
          </span>
        </div>
      )}
    </button>
  );
}

/**
 * One ranking row, as a host sees it. What the server sends a host is already
 * cut down to the ranking, the outreach log without names and whether the
 * member has been told about earning, so there is nothing else here to hide.
 */
function HostGuestRow({ guest, rank, scope, sort, eventID, onEvent, canReach, blockedReason, onExpand, onReach }) {
  const contact = outreachChip(guest.outreach);
  const contactLine = outreachLine(guest.outreach, eventID, { forHost: true });
  const toldAboutEarning = Boolean(guest.payouts?.eligible || guest.payouts?.enabled);
  return (
    <div className="p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="w-6 text-right text-sm text-gray-400 shrink-0">{rank}</span>
          <Face guest={guest} onExpand={onExpand} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                {guest.name || "Unnamed member"}
              </span>
              {guest.isHost && (
                <span className={`${chip} bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300`}>Host</span>
              )}
              {onEvent && (
                <span className={`${chip} bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300`}>
                  On your event
                </span>
              )}
              <span className={`${chip} ${contact.className}`}>{contact.label}</span>
              {toldAboutEarning && (
                <span className={`${chip} bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300`}>
                  Told about earning
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{statsLine(guest.stats, scope, sort)}</div>
            {contactLine && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{contactLine}</div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:flex sm:justify-end shrink-0">
          <button
            type="button"
            onClick={onReach}
            disabled={!canReach}
            title={blockedReason || "One text: an invite, a coupon, a note, or the earn nudge"}
            className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            Reach out
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Popular guests for a host an operator has given access to.
 *
 * The same ranking the operators work from, on the host's own event, with one
 * action: Reach out. No budget, no bookings, no payouts. The server enforces
 * all of that (the rows it sends a host carry nothing about money or selfies),
 * so this view only has to not draw what it does not have.
 */
export function HostPopularGuests({ eventID, eventData, onExplainerSeen }) {
  const { globalState } = useContext(GlobalContext);
  const hostID = globalState?.vendorID || globalState?.user?.userID || null;
  const senderName = globalState?.user?.name || "";
  // Coupons stay with the event's main host (utils/eventHosts.js), and the
  // coupon list refuses a co-host outright, so a co-host is not offered any.
  const coHost = Boolean(eventData) && !isPrimaryHost(eventData, hostID);
  const closedReason = eventClosedReason(eventData);
  // Why Reach out is off on every row, if it is.
  const blockedReason = !eventID ? "Save this event first" : CLOSED_MESSAGES[closedReason] || null;

  const [scope, setScope] = useState("all"); // "all" | "event"
  const [sort, setSort] = useState("viewers"); // "viewers" | "joinRate"
  // The join-rate floor the server used for the rows on screen.
  const [minBeforeJoining, setMinBeforeJoining] = useState(null);
  const [unmessagedOnly, setUnmessagedOnly] = useState(false);
  const [guests, setGuests] = useState([]);
  const [skippedDeleted, setSkippedDeleted] = useState(0);
  const [skippedMessaged, setSkippedMessaged] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [revoked, setRevoked] = useState(false);
  const [enlargedPhotoUser, setEnlargedPhotoUser] = useState(null);
  const [outreachTarget, setOutreachTarget] = useState(null);
  const [isSendingOutreach, setIsSendingOutreach] = useState(false);
  // Same reason as the operator tab: the event doc we were handed is a
  // snapshot, so someone invited from here would still look invitable.
  const [invitedNow, setInvitedNow] = useState(() => new Set());
  const [coupons, setCoupons] = useState(null); // null until first loaded
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [couponsError, setCouponsError] = useState("");
  // Bumped by every load, so a slow answer to an earlier one (a scope tapped
  // and then tapped back, or Refresh over the load an effect started) cannot
  // land its rows under the scope now showing.
  const loadSeq = useRef(0);

  const { vendorID, rsvpSitters, rsvpMovers, sitters, movers, invitedSitters } = eventData || {};
  // Same set as PopularGuestsSection and eventAttendeeIds on the server.
  const onEvent = useMemo(() => new Set([
    vendorID,
    ...(rsvpSitters || []),
    ...(rsvpMovers || []),
    ...(sitters || []),
    ...(movers || []),
    ...(invitedSitters || []),
    ...invitedNow,
  ].filter(Boolean)), [vendorID, rsvpSitters, rsvpMovers, sitters, movers, invitedSitters, invitedNow]);

  // Fetched when a host first opens Reach out, then kept. The list is every
  // coupon that works at this event, which includes Gist's own global codes, so
  // it is cut down to the ones a host may send before the picker sees it.
  const loadCoupons = useCallback(async () => {
    if (!eventID || coupons !== null || couponsLoading) return;
    if (coHost) {
      setCoupons([]);
      return;
    }
    setCouponsLoading(true);
    try {
      const data = await callFunction("adminListCoupons", { eventID }, { timeout: 60000 });
      const all = Array.isArray(data?.coupons) ? data.coupons : [];
      setCoupons(all.filter((c) => hostMayUseCoupon(c, { uid: hostID, eventID })));
    } catch (err) {
      console.error("adminListCoupons failed:", err);
      setCoupons([]);
      setCouponsError(err?.message || "Could not load your coupons for this event.");
    } finally {
      setCouponsLoading(false);
    }
  }, [eventID, coupons, couponsLoading, hostID, coHost]);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    const stale = () => seq !== loadSeq.current;
    setIsLoading(true);
    setLoadError(null);
    try {
      // Never `enabledOnly`: that is the operators' payouts list, and the
      // server refuses it to a host.
      const ranking = await callFunction("listPopularGuests", {
        limit: LIST_LIMIT,
        ...(scope === "event" && eventID ? { eventID } : {}),
        ...(eventID ? { contextEventID: eventID } : {}),
        unmessagedOnly,
        sort,
      }, { timeout: 60000 });
      if (stale()) return;
      setRevoked(false);
      setGuests(Array.isArray(ranking?.guests) ? ranking.guests : []);
      setSkippedDeleted(ranking?.skippedDeleted || 0);
      setSkippedMessaged(ranking?.skippedMessaged || 0);
      setMinBeforeJoining(ranking?.minBeforeJoining || null);
    } catch (err) {
      if (stale()) return;
      console.error("listPopularGuests failed:", err);
      setGuests([]);
      // On "All events" nothing about the request is event-specific, so any
      // refusal there is the grant itself being gone.
      if (isAccessRevoked(err) || (scope === "all" && err?.code === "functions/permission-denied")) {
        setRevoked(true);
      } else {
        setLoadError(err?.message || "Could not load popular guests.");
      }
    } finally {
      if (!stale()) setIsLoading(false);
    }
  }, [scope, sort, eventID, unmessagedOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const patchGuest = (userID, patch) =>
    setGuests((prev) => prev.map((g) => (g.userID === userID ? { ...g, ...patch } : g)));

  const openOutreach = (guest) => {
    setOutreachTarget(guest);
    loadCoupons();
  };

  // One text, assembled server-side from what the host ticked. The server
  // checks the event is theirs and still ahead, the coupon is theirs, and the
  // daily limits, and says which one it was when it refuses.
  const sendOutreach = async ({ message, includeEarn, inviteToEvent, couponCode }) => {
    const guest = outreachTarget;
    if (!guest || !eventID) return;
    setIsSendingOutreach(true);
    try {
      const data = await callFunction(
        "sendGuestPayoutInvite",
        {
          userID: guest.userID,
          eventID,
          ...(message ? { message } : {}),
          ...(couponCode ? { couponCode } : {}),
          includeEarn,
          inviteToEvent,
        },
        { timeout: 60000 }
      );
      if (includeEarn) {
        patchGuest(guest.userID, {
          payouts: { ...guest.payouts, eligible: true, invitedAt: new Date().toISOString() },
        });
      }
      if (data?.outreach) patchGuest(guest.userID, { outreach: data.outreach });
      showToast(
        data?.message ? `${guest.name || "Member"}: ${data.message}` : "Sent",
        { type: data?.success ? "success" : "warning" }
      );
      setOutreachTarget(null);
      if (data?.invitedToEvent === "yes") {
        setInvitedNow((prev) => new Set(prev).add(guest.userID));
      }
    } catch (err) {
      console.error("sendGuestPayoutInvite failed:", err);
      if (isAccessRevoked(err)) {
        setOutreachTarget(null);
        setGuests([]);
        setRevoked(true);
      } else {
        showToast(err?.message || "Could not send", { type: "error" });
      }
    } finally {
      setIsSendingOutreach(false);
    }
  };

  const activeScope = SCOPES.find((s) => s.key === scope) || SCOPES[0];
  const byRate = sort === "joinRate";
  const floorNote = byRate && !isLoading ? joinRateFloorNote(scope, minBeforeJoining) : null;

  if (revoked) {
    return (
      <div className="p-6 border border-dashed border-gray-300 dark:border-gray-600 rounded-md text-center">
        <p className="text-sm text-gray-600 dark:text-gray-300">{REVOKED_MESSAGE}</p>
      </div>
    );
  }

  return (
    <div>
      <PopularExplainer onSeen={onExplainerSeen} />

      <div className="flex items-start justify-between gap-3 mb-3">
        <p className="text-sm text-gray-600 dark:text-gray-300">{byRate ? activeScope.rateBlurb : activeScope.blurb}</p>
        <button
          type="button"
          onClick={load}
          disabled={isLoading}
          className="text-sm underline text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 shrink-0 disabled:no-underline disabled:cursor-not-allowed"
        >
          {isLoading ? "Loading..." : "Refresh"}
        </button>
      </div>

      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg mb-3">
        {SCOPES.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setScope(s.key)}
            aria-pressed={scope === s.key}
            className={`flex-1 px-2 sm:px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${scope === s.key
              ? "bg-white dark:bg-gray-900 shadow text-gray-900 dark:text-gray-100"
              : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <PopularListControls
        sort={sort}
        onSortChange={setSort}
        unmessagedOnly={unmessagedOnly}
        onUnmessagedChange={setUnmessagedOnly}
        skippedMessaged={skippedMessaged}
        isLoading={isLoading}
      />
      {floorNote && <p className="-mt-1 mb-3 text-xs text-gray-500 dark:text-gray-400">{floorNote}</p>}

      {blockedReason && eventID && (
        <p className="mb-3 p-3 rounded-md bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-200">
          {blockedReason}
        </p>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400 text-sm">
          Loading popular guests...
        </div>
      )}

      {!isLoading && skippedDeleted > 0 && (
        <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
          {skippedDeleted} deleted {skippedDeleted === 1 ? "account was" : "accounts were"} skipped.
        </p>
      )}

      {!isLoading && loadError && (
        <div className="mb-3 p-3 rounded-md bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 text-sm">
          {loadError}
        </div>
      )}

      {!isLoading && !loadError && guests.length === 0 && (
        <div className="p-6 border border-dashed border-gray-300 dark:border-gray-600 rounded-md text-center">
          <p className="text-sm text-gray-600 dark:text-gray-300">{(byRate && activeScope.rateEmpty) || activeScope.empty}</p>
        </div>
      )}

      {/* Page scroll on a phone, an inner scroller on a desktop: same reason
          as the operator tab. */}
      <div className="flex flex-col gap-2 sm:max-h-[calc(100dvh-300px)] sm:overflow-y-auto">
        {(isLoading ? [] : guests).map((guest, index) => (
          <HostGuestRow
            key={guest.userID}
            guest={guest}
            rank={index + 1}
            scope={scope}
            sort={sort}
            eventID={eventID}
            onEvent={onEvent.has(guest.userID)}
            canReach={!blockedReason && !isSendingOutreach}
            blockedReason={blockedReason}
            onExpand={() => setEnlargedPhotoUser(guest)}
            onReach={() => openOutreach(guest)}
          />
        ))}
      </div>

      {outreachTarget && (
        <GuestOutreachDialog
          mode="host"
          senderName={senderName}
          guest={outreachTarget}
          eventName={eventData?.name}
          eventID={eventID}
          coupons={coupons}
          couponsLoading={couponsLoading}
          couponsError={couponsError}
          coHost={coHost}
          earnBlocked={hostEarnBlockedReason(outreachTarget, hostID)}
          alreadyOnEvent={onEvent.has(outreachTarget.userID)}
          isSending={isSendingOutreach}
          onCancel={() => setOutreachTarget(null)}
          onConfirm={sendOutreach}
        />
      )}

      <EnlargedPhotoModal user={enlargedPhotoUser} onClose={() => setEnlargedPhotoUser(null)} />
    </div>
  );
}

export default HostPopularGuests;
